import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction
} from "react";
import { ProvetDepartment, Reason, ReasonGroup, Species } from "../types";
import { applyDepartmentSettings } from "../utilities/departmentUtilities";
import { useProvetStatic } from "./provetStaticContext";

/**
 * Loading state of a list fetched from the API.
 * "error" means the request failed, which is different from "loaded" with an empty list.
 */
type ProvetLoadStatus = "loading" | "loaded" | "error";

/**
 * Data context for Provet booking.
 * Contains departments, reasons, and reason groups that change infrequently.
 * Separated from booking flow state to prevent unnecessary re-renders.
 *
 * The lists are fetched once, here, when the booking form mounts. The clinic and
 * treatment finders read them (and their load status) from this context and call
 * the reload functions for "Try again".
 *
 * The species only label the treatments that are for certain animals, so if they fail to
 * load the treatments are shown without that label, not with an error.
 */
interface ProvetDataContextType {
  departments: ProvetDepartment[];
  departmentsStatus: ProvetLoadStatus;
  reloadDepartments: () => void;
  reasonGroups: ReasonGroup[];
  reasons: Reason[];
  reasonsStatus: ProvetLoadStatus;
  reloadReasons: () => void;
  species: Species[];
  visitedClinicBookingProvetId?: number;
  setVisitedClinicBookingProvetId: Dispatch<SetStateAction<number | undefined>>;
  visitedTreatmentProvetId?: number;
  setVisitedTreatmentProvetId: Dispatch<SetStateAction<number | undefined>>;
}

interface ProvetDataProviderProps {
  children: ReactNode;
  visitedClinicBookingProvetId?: number;
  visitedTreatmentProvetId?: number;
}

const ProvetDataContext = createContext<ProvetDataContextType | undefined>(undefined);

export const ProvetDataProvider = ({
  children,
  visitedClinicBookingProvetId: initialVisitedClinicBookingProvetId,
  visitedTreatmentProvetId: initialVisitedTreatmentProvetId
}: ProvetDataProviderProps) => {
  const { api, activeDepartmentIds, excludedDepartmentIds } = useProvetStatic();

  const [fetchedDepartments, setFetchedDepartments] = useState<ProvetDepartment[]>([]);
  const [departmentsStatus, setDepartmentsStatus] = useState<ProvetLoadStatus>("loading");
  const [reasonGroups, setReasonGroups] = useState<ReasonGroup[]>([]);
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [reasonsStatus, setReasonsStatus] = useState<ProvetLoadStatus>("loading");
  const [species, setSpecies] = useState<Species[]>([]);
  const [visitedClinicBookingProvetId, setVisitedClinicBookingProvetId] = useState<
    number | undefined
  >(initialVisitedClinicBookingProvetId);
  const [visitedTreatmentProvetId, setVisitedTreatmentProvetId] = useState<
    number | undefined
  >(initialVisitedTreatmentProvetId);

  const loadDepartments = useCallback(async () => {
    setDepartmentsStatus("loading");

    try {
      setFetchedDepartments((await api.fetchDepartments()) ?? []);
      setDepartmentsStatus("loaded");
    } catch {
      // Any failure, an abort (e.g. a timeout) included, shows the error with "Try again"
      setDepartmentsStatus("error");
    }
  }, [api]);

  const loadReasons = useCallback(async () => {
    setReasonsStatus("loading");

    try {
      const [fetchedReasonGroups, fetchedReasons] = await Promise.all([
        api.fetchReasonGroups(),
        api.fetchReasons()
      ]);

      setReasonGroups(fetchedReasonGroups ?? []);
      setReasons(fetchedReasons ?? []);
      setReasonsStatus("loaded");
    } catch {
      // Any failure, an abort (e.g. a timeout) included, shows the error with "Try again"
      setReasonsStatus("error");
    }
  }, [api]);

  const loadSpecies = useCallback(async () => {
    try {
      setSpecies((await api.fetchSpecies()) ?? []);
    } catch {
      // Already logged. The treatments are then shown without the animals they are for.
      setSpecies([]);
    }
  }, [api]);

  // Fetch the lists once on mount. Departments and treatments load independently,
  // so one failing doesn't hide the other.
  useEffect(() => {
    loadDepartments();
    loadReasons();
    loadSpecies();
  }, [loadDepartments, loadReasons, loadSpecies]);

  // Joined so a new array with the same ids (e.g. a default `[]`) doesn't rebuild the list
  const activeIdsKey = activeDepartmentIds.join(",");
  const excludedIdsKey = excludedDepartmentIds.join(",");

  const departments = useMemo(
    () =>
      applyDepartmentSettings(
        fetchedDepartments,
        activeDepartmentIds,
        excludedDepartmentIds
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchedDepartments, activeIdsKey, excludedIdsKey]
  );

  const value = useMemo(
    () => ({
      departments,
      departmentsStatus,
      reloadDepartments: loadDepartments,
      reasonGroups,
      reasons,
      reasonsStatus,
      reloadReasons: loadReasons,
      species,
      visitedClinicBookingProvetId,
      setVisitedClinicBookingProvetId,
      visitedTreatmentProvetId,
      setVisitedTreatmentProvetId
    }),
    [
      departments,
      departmentsStatus,
      loadDepartments,
      reasonGroups,
      reasons,
      reasonsStatus,
      loadReasons,
      species,
      visitedClinicBookingProvetId,
      visitedTreatmentProvetId
    ]
  );

  return (
    <ProvetDataContext.Provider value={value}>{children}</ProvetDataContext.Provider>
  );
};

export const useProvetData = () => {
  const context = useContext(ProvetDataContext);
  if (context === undefined) {
    throw new Error("useProvetData must be used within a ProvetDataProvider");
  }
  return context;
};

export type { ProvetDataContextType, ProvetLoadStatus };
